/**
 * Horizon overrides for Shopify.actions and MITCH accelerated checkout support.
 */

function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('theme-drawer#cart-drawer') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      /** @type {HTMLElement & {open?: () => void} | null} */
      const drawer = document.querySelector('theme-drawer#cart-drawer');

      if (drawer?.open) {
        drawer.open();
      } else {
        window.location.href = Theme.routes.cart_url || '/cart';
      }
    },
  });
}

const acceleratedCheckoutSelector =
  '.additional-checkout-buttons, .accelerated-checkout-block, shopify-accelerated-checkout, shopify-accelerated-checkout-cart, [data-shopify="payment-button"]';

let acceleratedCheckoutRefreshTimer;
let acceleratedCheckoutRetryTimer;
let acceleratedCheckoutRetryCount = 0;

function hasAcceleratedCheckout(root = document) {
  if (!(root instanceof Document || root instanceof Element || root instanceof DocumentFragment)) return false;
  if (root instanceof Element && root.matches(acceleratedCheckoutSelector)) return true;
  return Boolean(root.querySelector(acceleratedCheckoutSelector));
}

function refreshAcceleratedCheckout() {
  acceleratedCheckoutRefreshTimer = undefined;
  if (!hasAcceleratedCheckout()) return;

  const paymentButton = window.Shopify?.PaymentButton;
  if (typeof paymentButton?.init === 'function') {
    clearTimeout(acceleratedCheckoutRetryTimer);
    acceleratedCheckoutRetryTimer = undefined;
    acceleratedCheckoutRetryCount = 0;

    try {
      paymentButton.init();
    } catch (error) {
      console.warn('[accelerated-checkout] Payment button refresh failed:', error);
    }
    return;
  }

  if (acceleratedCheckoutRetryCount >= 12) {
    acceleratedCheckoutRetryCount = 0;
    return;
  }

  acceleratedCheckoutRetryCount += 1;
  clearTimeout(acceleratedCheckoutRetryTimer);
  acceleratedCheckoutRetryTimer = window.setTimeout(refreshAcceleratedCheckout, 125);
}

function scheduleAcceleratedCheckoutRefresh() {
  clearTimeout(acceleratedCheckoutRefreshTimer);
  acceleratedCheckoutRefreshTimer = window.setTimeout(() => {
    requestAnimationFrame(() => requestAnimationFrame(refreshAcceleratedCheckout));
  }, 40);
}

const paymentReassuranceTranslations = {
  ar: {
    title: 'وسائل الدفع - شركاء موثوقون',
    ideal: 'دفع مصرفي',
    note: 'يتوفر PayPal 4X وKlarna 3X حسب الأهلية. يظهر iDEAL عند الدفع عندما يكون متاحاً.',
  },
  de: {
    title: 'ZAHLUNGSARTEN - VERTRAUENSPARTNER',
    ideal: 'Bankzahlung',
    note: 'PayPal 4X und Klarna 3X sind je nach Berechtigung verfügbar. iDEAL wird beim Bezahlen angezeigt, wenn es verfügbar ist.',
  },
  en: {
    title: 'PAYMENT OPTIONS - TRUSTED PARTNERS',
    ideal: 'Bank payment',
    note: 'PayPal 4X and Klarna 3X are available subject to eligibility. iDEAL appears at checkout when available.',
  },
  es: {
    title: 'MÉTODOS DE PAGO - SOCIOS DE CONFIANZA',
    ideal: 'Pago bancario',
    note: 'PayPal 4X y Klarna 3X están disponibles según elegibilidad. iDEAL aparece al pagar cuando está disponible.',
  },
  fr: {
    title: 'MOYENS DE PAIEMENT - PARTENAIRES DE CONFIANCE',
    ideal: 'Paiement bancaire',
    note: 'PayPal 4X et Klarna 3X sont proposés selon éligibilité. iDEAL s’affiche au paiement lorsqu’il est disponible.',
  },
  it: {
    title: 'METODI DI PAGAMENTO - PARTNER DI FIDUCIA',
    ideal: 'Pagamento bancario',
    note: 'PayPal 4X e Klarna 3X sono disponibili in base all’idoneità. iDEAL viene mostrato al pagamento quando disponibile.',
  },
  nl: {
    title: 'BETAALMETHODEN - VERTROUWDE PARTNERS',
    ideal: 'Bankbetaling',
    note: 'PayPal 4X en Klarna 3X zijn beschikbaar afhankelijk van geschiktheid. iDEAL verschijnt bij het afrekenen wanneer beschikbaar.',
  },
  'pt-PT': {
    title: 'MÉTODOS DE PAGAMENTO - PARCEIROS DE CONFIANÇA',
    ideal: 'Pagamento bancário',
    note: 'PayPal 4X e Klarna 3X estão disponíveis consoante a elegibilidade. O iDEAL aparece no pagamento quando disponível.',
  },
  tr: {
    title: 'ÖDEME YÖNTEMLERİ - GÜVENİLİR ORTAKLAR',
    ideal: 'Banka ödemesi',
    note: 'PayPal 4X ve Klarna 3X uygunluğa bağlı olarak sunulur. iDEAL mevcut olduğunda ödeme adımında görünür.',
  },
};

function getStorefrontLocale() {
  const rawLocale = (document.documentElement.lang || 'en').trim();
  if (paymentReassuranceTranslations[rawLocale]) return rawLocale;

  const baseLocale = rawLocale.split('-')[0];
  return paymentReassuranceTranslations[baseLocale] ? baseLocale : 'en';
}

function ensureIdealPaymentStyles() {
  if (document.getElementById('mitch-ideal-payment-styles')) return;

  const style = document.createElement('style');
  style.id = 'mitch-ideal-payment-styles';
  style.textContent = `
    .conversion-reassurance__payment-grid.mitch-payment-grid--with-ideal {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 8px;
    }

    .conversion-reassurance__payment-option {
      position: relative;
      overflow: hidden;
    }

    .mitch-payment-brand {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: fit-content;
      min-height: 28px;
      padding: 5px 9px;
      border-radius: 5px;
      font-size: 12px;
      font-weight: 800;
      line-height: 1;
      letter-spacing: -.01em;
      box-shadow: 0 1px 0 rgba(0,0,0,.08);
    }

    .mitch-payment-subline {
      display: block;
      margin-top: 6px;
      font-size: 10px;
      line-height: 1.25;
    }

    .conversion-reassurance__payment-option--paypal .mitch-payment-brand {
      background: #ffffff;
      color: #003087;
      border: 1px solid rgba(0,48,135,.18);
    }

    .conversion-reassurance__payment-option--paypal .mitch-paypal-light {
      color: #009cde;
    }

    .conversion-reassurance__payment-option--klarna .mitch-payment-brand {
      background: #ffb3c7;
      color: #000000;
      border: 1px solid rgba(0,0,0,.08);
    }

    .conversion-reassurance__payment-option--ideal .mitch-payment-brand {
      background: #ffffff;
      color: #000000;
      border: 1px solid #cc0066;
    }

    .conversion-reassurance__payment-option--ideal .mitch-ideal-accent {
      color: #cc0066;
    }

    @media (max-width: 680px) {
      .conversion-reassurance__payment-grid.mitch-payment-grid--with-ideal {
        grid-template-columns: 1fr;
      }
    }
  `;
  document.head.appendChild(style);
}

function decoratePaymentOption(option, brand, sublineText) {
  if (!option) return;

  option.classList.remove(
    'conversion-reassurance__payment-option--paypal',
    'conversion-reassurance__payment-option--klarna',
    'conversion-reassurance__payment-option--ideal'
  );
  option.classList.add(`conversion-reassurance__payment-option--${brand}`);

  const brandEl = document.createElement('strong');
  brandEl.className = 'mitch-payment-brand';

  if (brand === 'paypal') {
    brandEl.innerHTML = 'Pay<span class="mitch-paypal-light">Pal</span> 4X';
  } else if (brand === 'klarna') {
    brandEl.textContent = 'Klarna 3X';
  } else {
    brandEl.innerHTML = '<span class="mitch-ideal-accent">i</span>DEAL';
  }

  const subline = document.createElement('span');
  subline.className = 'mitch-payment-subline';
  subline.textContent = sublineText || '';

  option.replaceChildren(brandEl, subline);
}

function integrateIdealPaymentOption(root = document) {
  if (!(root instanceof Document || root instanceof Element || root instanceof DocumentFragment)) return;

  const sections = [];
  if (root instanceof Element && root.matches('.conversion-reassurance--cart')) {
    sections.push(root);
  }
  if (typeof root.querySelectorAll === 'function') {
    sections.push(...root.querySelectorAll('.conversion-reassurance--cart'));
  }
  if (!sections.length) return;

  ensureIdealPaymentStyles();
  const locale = getStorefrontLocale();
  const copy = paymentReassuranceTranslations[locale] || paymentReassuranceTranslations.en;

  for (const section of [...new Set(sections)]) {
    const title = section.querySelector('.conversion-reassurance__payment-title');
    const grid = section.querySelector('.conversion-reassurance__payment-grid');
    const note = section.querySelector('.conversion-reassurance__payment-note');
    if (!grid) continue;

    if (title) title.textContent = copy.title;
    if (note) note.textContent = copy.note;
    grid.classList.add('mitch-payment-grid--with-ideal');

    const existingOptions = [...grid.querySelectorAll('.conversion-reassurance__payment-option')];
    let paypalOption = existingOptions.find((option) => /paypal/i.test(option.textContent));
    let klarnaOption = existingOptions.find((option) => /klarna/i.test(option.textContent));

    if (paypalOption) {
      const text = paypalOption.querySelector('span')?.textContent?.trim() || '';
      decoratePaymentOption(paypalOption, 'paypal', text);
    }

    if (klarnaOption) {
      const text = klarnaOption.querySelector('span')?.textContent?.trim() || '';
      decoratePaymentOption(klarnaOption, 'klarna', text);
    }

    let idealOption = grid.querySelector('[data-mitch-payment="ideal"]');
    if (!idealOption) {
      idealOption = document.createElement('div');
      idealOption.className = 'conversion-reassurance__payment-option';
      idealOption.dataset.mitchPayment = 'ideal';
      grid.appendChild(idealOption);
    }

    decoratePaymentOption(idealOption, 'ideal', copy.ideal);
  }
}

function schedulePaymentReassuranceRefresh(root = document) {
  requestAnimationFrame(() => integrateIdealPaymentOption(root));
}

const acceleratedCheckoutObserver = new MutationObserver((mutations) => {
  let shouldRefreshCheckout = false;
  let shouldRefreshReassurance = false;

  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (hasAcceleratedCheckout(node)) shouldRefreshCheckout = true;
      if (node.matches('.conversion-reassurance--cart') || node.querySelector('.conversion-reassurance--cart')) {
        shouldRefreshReassurance = true;
      }
    }
  }

  if (shouldRefreshCheckout) scheduleAcceleratedCheckoutRefresh();
  if (shouldRefreshReassurance) schedulePaymentReassuranceRefresh();
});

acceleratedCheckoutObserver.observe(document.documentElement, {
  childList: true,
  subtree: true,
});

document.addEventListener('shopify:section:load', () => {
  scheduleAcceleratedCheckoutRefresh();
  schedulePaymentReassuranceRefresh();
});

document.addEventListener('DOMContentLoaded', () => {
  scheduleAcceleratedCheckoutRefresh();
  schedulePaymentReassuranceRefresh();
}, { once: true });

if (window.Shopify?.actions) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}
